/**
 * La burbuja de WhatsApp de TBit Jobs en jobs.tbit.app (2026-10-08): el botón
 * propio de TBit (`tbit.app/js/whatsapp-button.js`, el mismo del generador
 * /whatsapp-widget), no uno de terceros, apuntando a la línea del producto
 * (+1 555-932-8437), que atienden su agente de IA (chat) y su asistente de voz.
 *
 * Dónde sale y con qué primer mensaje lo decide la ruta. Las páginas de tbit.app
 * (/es/job-apply y /es/job-apply/socios) llevan la misma burbuja desde el sitio.
 */
import type { Locale } from '~/i18n/locale';
import { messages } from '~/i18n';

export const JOBS_WHATSAPP = '15559328437';
export const WHATSAPP_BUTTON_SRC = 'https://tbit.app/js/whatsapp-button.js';
/** El agente no tiene foto propia: la mascota de la app. */
export const JOBS_AVATAR = 'https://jobs.tbit.app/brand/mascot-256.png';
/** Los ids que pone el script; quitarlos es esconder la burbuja. */
export const BUBBLE_IDS = ['tbit-whatsapp-button', 'tbit-whatsapp-widget'] as const;

export type BubbleAudience = 'candidates' | 'employers' | 'partners';

export interface BubblePlacement {
  audience: BubbleAudience;
  /** La landing de agencias trae su idioma en la ruta; el resto, el de la app. */
  locale: Locale | null;
  /** En la app con sesión, solo el ícono: tapa menos en un teléfono. */
  compact: boolean;
}

const AGENCY_LANDINGS: Record<string, Locale> = { '/agencias': 'es', '/empresas': 'es', '/agencies': 'en' };

/**
 * Dónde va la burbuja y para quién, o null donde no va: la consola de
 * operación y la de agencia (otra audiencia, otro soporte), el login y su
 * regreso, el hilo de un mensaje (la caja de respuesta va abajo y la burbuja
 * taparía «Enviar») y el plan (es el checkout: nada encima del botón de pagar).
 */
export function bubblePlacement(pathname: string): BubblePlacement | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path in AGENCY_LANDINGS) return { audience: 'employers', locale: AGENCY_LANDINGS[path], compact: false };
  if (/^\/(admin|agency|auth|login)(\/|$)/.test(path)) return null;
  if (/^\/mensajes\/[^/]+/.test(path)) return null;
  if (path === '/perfil/plan') return null;
  if (path === '/socios' || path === '/perfil/socios') return { audience: 'partners', locale: null, compact: true };
  return { audience: 'candidates', locale: null, compact: true };
}

/** Lo que recibe `whatsappButton()`. */
export function bubbleConfig(audience: BubbleAudience, locale: Locale, compact: boolean) {
  const t = messages(locale).common.whatsappBubble;
  return {
    locale,
    phoneNumber: JOBS_WHATSAPP,
    buttonName: t.buttonName,
    brandName: 'TBit Jobs',
    brandSubtitleText: t.brandSubtitle,
    callToAction: t.callToAction,
    welcomeMessage: t.welcome,
    prefillMessage: t.prefill[audience],
    brandImageUrl: JOBS_AVATAR,
    buttonPosition: 'right',
    buttonStyle: compact ? 'icon-only' : 'icon-with-text',
  };
}

type WhatsappButton = (config: ReturnType<typeof bubbleConfig>) => void;
declare global {
  interface Window {
    whatsappButton?: WhatsappButton;
  }
}

let loading: Promise<WhatsappButton> | null = null;

/** Baja el script UNA vez (después de pintar la app) y devuelve `whatsappButton`. */
export function loadWhatsappButton(): Promise<WhatsappButton> {
  if (window.whatsappButton) return Promise.resolve(window.whatsappButton);
  if (loading) return loading;
  loading = new Promise<WhatsappButton>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WHATSAPP_BUTTON_SRC;
    script.async = true;
    script.onload = () => (window.whatsappButton ? resolve(window.whatsappButton) : reject(new Error('whatsappButton missing')));
    script.onerror = () => reject(new Error('whatsapp-button.js failed'));
    document.head.appendChild(script);
  });
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

export function removeBubble(): void {
  for (const id of BUBBLE_IDS) document.getElementById(id)?.remove();
}

/** Para pruebas. */
export function resetWhatsappButtonLoader(): void {
  loading = null;
}
